// src/pages/student/SuggestedSupervisors.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    Card,
    CardContent,
    CardMedia,
    CardActions,
    Avatar,
    Rating,
    LinearProgress,
    useTheme,
    InputAdornment,
    Tooltip,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Fade,
    Zoom,
    Divider,
    TablePagination,
    Badge,
    SpeedDial,
    SpeedDialAction,
    SpeedDialIcon,
    Stack,
    useMediaQuery
} from '@mui/material';
import {
    Search,
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    Close as CloseIcon,
    FilterList as FilterIcon,
    CheckCircle,
    Cancel,
    Warning,
    Schedule,
    Person,
    School,
    Work,
    Psychology,
    TrendingUp,
    TrendingDown,
    TrendingFlat,
    Analytics,
    Assessment,
    Description,
    Lightbulb,
    DateRange,
    ExpandMore,
    ClearAll,
    ThumbUp,
    ThumbDown,
    Info,
    Star,
    BarChart,
    Timeline,
    Email,
    Phone,
    LocationOn,
    Check,
    Close,
    Send,
    Assignment,
    Speed,
    Feedback,
    Book,
    MenuBook
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APISupervisorMatchRecommendations,
    APISupervisorMatchUpdateChoice,
    APISupervisorMatchShow
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails,
    recordsCount
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
// NOTE: The component file is still named StudentBar.js
// but it now renders "Researcher Portal" as its visible title.
import StudentBar from '../../components/StudentBar';
import ErrorState from '../../components/ErrorState';
import authService from '../../apiServices/authService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Researcher choice options
const choiceOptions = [
    { id: 'selected', name: 'Select Supervisor', color: 'success', icon: <CheckCircle />, action: 'select' },
    { id: 'declined', name: 'Decline', color: 'error', icon: <Cancel />, action: 'decline' },
    { id: 'requested_change', name: 'Request Change', color: 'info', icon: <Feedback />, action: 'request' }
];

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const SuggestedSupervisors = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [recommendations, setRecommendations] = useState([]);
    const [selectedMatch, setSelectedMatch] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const isMounted = useRef(true);
    const studentId = getUserDetails("Id");

    // Handle logout
    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    // Filters
    const [filters, setFilters] = useState({
        min_score: '',
        availability: ''
    });

    // Dialog states
    const [filterOptions, setFilterOptions] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openChoiceDialog, setOpenChoiceDialog] = useState(false);
    const [selectedChoice, setSelectedChoice] = useState(null);
    const [studentNotes, setStudentNotes] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [stats, setStats] = useState({
        total_matches: 0,
        pending_review: 0,
        selected: 0,
        average_match_score: 0
    });

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchRecommendations();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchRecommendations = async () => {
        setLoading(true);
        setError(null);

        try {
            const result = await apiService.listById(
                APISupervisorMatchRecommendations, 
                studentId
            );

            if (result.success && isMounted.current) {
                const data = Array.isArray(result.data) ? result.data : [];
                setRecommendations(data);
                
                // Calculate stats
                setStats({
                    total_matches: data.length,
                    pending_review: data.filter(r => r.recommendation_status === 'pending_review').length,
                    selected: data.filter(r => r.student_choice === 'selected').length,
                    average_match_score: data.reduce((acc, r) => acc + (r.match_score || 0), 0) / (data.length || 1)
                });
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load recommendations");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const handleOpenView = async (match) => {
        setSelectedMatch(match);
        setOpenView(true);
        
        // Fetch detailed data
        try {
            const result = await apiService.show(APISupervisorMatchShow, match.id);
            if (result.success && result.data) {
                setSelectedMatch(result.data);
            }
        } catch (error) {
            console.error("Error fetching details:", error);
        }
    };

    const handleCloseView = () => {
        setOpenView(false);
        setSelectedMatch(null);
    };

    const handleOpenChoiceDialog = (match, choice) => {
        setSelectedMatch(match);
        setSelectedChoice(choice);
        setStudentNotes('');
        setOpenChoiceDialog(true);
    };

    const handleCloseChoiceDialog = () => {
        setOpenChoiceDialog(false);
        setSelectedMatch(null);
        setSelectedChoice(null);
        setStudentNotes('');
    };

    const handleSubmitChoice = async () => {
        if (!selectedMatch || !selectedChoice) return;
        
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('updateId', selectedMatch.id);
            formData.append('student_choice', selectedChoice.id);
            if (studentNotes) {
                formData.append('student_notes', studentNotes);
            }
            
            const result = await apiService.update(APISupervisorMatchUpdateChoice, formData);
            
            if (result && result.success) {
                showToast("Success", `You have ${selectedChoice.name.toLowerCase()} this supervisor`, "success");
                fetchRecommendations();
                handleCloseChoiceDialog();
            }
        } catch (error) {
            showToast("Error", "Failed to update choice", "error");
        } finally {
            setActionLoading(false);
        }
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleCloseOptions = () => setFilterOptions(false);

    const clearFilters = () => {
        setFilters({
            min_score: '',
            availability: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    // Filtered data
    const filteredData = recommendations.filter(record => {
        const supervisor = record.supervisor || {};
        const matchesSearch = searchTerm === '' ||
            supervisor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            supervisor.university?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            supervisor.academic_title?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesMinScore = !filters.min_score || (record.match_score >= parseInt(filters.min_score));
        const matchesAvailability = !filters.availability || 
            (filters.availability === 'available' && record.availability_status) ||
            (filters.availability === 'unavailable' && !record.availability_status);

        return matchesSearch && matchesMinScore && matchesAvailability;
    });

    // Get score color
    const getScoreColor = (score) => {
        if (score >= 80) return 'success';
        if (score >= 60) return 'info';
        if (score >= 40) return 'warning';
        return 'error';
    };

    if (loading) {
        return <LoadingState message="Loading Supervisor Recommendations..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchRecommendations} title="Unable to Load Recommendations" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer>
                <Box sx={{backgroundColor: 'white',borderRadius: 3 }}>
                    <StudentBar notificationCount={11} />
                    {/* Header Section */}
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                        }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2, 
                            flexWrap: 'wrap',
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h6" sx={{
                                fontWeight: 'bold',
                                color: theme.palette.primary.main,
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <Psychology /> Suggested Supervisors ({recordsCount(recommendations.length)})
                            </Typography>

                            <Box sx={{ 
                                display: 'flex', 
                                gap: 1, 
                                flexShrink: 0,
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: { xs: 'center', sm: 'flex-start' }
                            }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<FilterIcon />}
                                    onClick={() => setFilterOptions(true)}
                                    sx={{ 
                                        textTransform: 'none', 
                                        borderRadius: '8px',
                                        flex: { xs: 1, sm: 'none' }
                                    }}
                                >
                                    Filter
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="info"
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchRecommendations}
                                    sx={{ 
                                        textTransform: 'none', 
                                        borderRadius: '8px',
                                        flex: { xs: 1, sm: 'none' }
                                    }}
                                >
                                    Refresh
                                </Button>
                            </Box>

                            {/* Search Bar */}
                            <Paper sx={{
                                width: { xs: '100%', sm: '300px' },
                                p: '2px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                marginLeft: { xs: 0, sm: 'auto' },
                                borderRadius: '8px',
                                backgroundColor: theme.palette.grey[50],
                            }}>
                                <TextField
                                    fullWidth
                                    variant="standard"
                                    placeholder="Search by name, university, title..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        disableUnderline: true,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search sx={{ color: theme.palette.grey[600] }} />
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            '& .MuiInputBase-input': {
                                                padding: '10px 0',
                                            }
                                        }
                                    }}
                                    size="small"
                                />
                            </Paper>

                        </Box>

                        {/* Stats Cards */}
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ 
                                    textAlign: 'center', 
                                    bgcolor: theme.palette.primary.light + '10',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' }
                                }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold', 
                                            color: 'primary.main',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {stats.total_matches}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                            Total Matches
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ 
                                    textAlign: 'center', 
                                    bgcolor: theme.palette.warning.light + '10',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' }
                                }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold', 
                                            color: 'warning.main',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {stats.pending_review}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                            Pending Review
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ 
                                    textAlign: 'center', 
                                    bgcolor: theme.palette.success.light + '10',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' }
                                }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold', 
                                            color: 'success.main',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {stats.selected}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                            Selected
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ 
                                    textAlign: 'center', 
                                    bgcolor: theme.palette.info.light + '10',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' }
                                }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold', 
                                            color: 'info.main',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {Math.round(stats.average_match_score)}%
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                            Avg Match Score
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Paper>
                </Box>
                {/* Recommendations Grid */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    {filteredData.length === 0 ? (
                        <Paper sx={{ 
                            p: { xs: 4, sm: 8 }, 
                            textAlign: 'center', 
                            borderRadius: 3,
                            backgroundColor: 'white'
                        }}>
                            <Psychology sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                No supervisor recommendations found
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Try adjusting your search or filter criteria
                            </Typography>
                        </Paper>
                    ) : (
                        <Grid container spacing={2}>
                            {filteredData.map((record, index) => {
                                const supervisor = record.supervisor || {};
                                const scoreColor = getScoreColor(record.match_score);
                                
                                return (
                                    <Grid item xs={12} sm={6} lg={4} key={record.id}>
                                        <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                                            <Card sx={{ 
                                                height: '100%', 
                                                display: 'flex', 
                                                flexDirection: 'column',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: theme.shadows[8]
                                                }
                                            }}>
                                                {/* Match Score Badge */}
                                                <Box sx={{ 
                                                    position: 'absolute', 
                                                    top: 16, 
                                                    right: 16, 
                                                    zIndex: 1
                                                }}>
                                                    <Tooltip title="Match Score">
                                                        <Chip
                                                            label={`${record.match_score}%`}
                                                            color={scoreColor}
                                                            icon={<TrendingUp />}
                                                            sx={{ fontWeight: 'bold' }}
                                                        />
                                                    </Tooltip>
                                                </Box>

                                                {/* Supervisor Avatar and Info */}
                                                <CardContent sx={{ flexGrow: 1, pt: 3, p: { xs: 2, sm: 3 } }}>
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        gap: 2, 
                                                        mb: 2,
                                                        flexDirection: { xs: 'column', sm: 'row' },
                                                        textAlign: { xs: 'center', sm: 'left' }
                                                    }}>
                                                        <Avatar
                                                            src={supervisor.profile_image}
                                                            sx={{ 
                                                                width: { xs: 60, sm: 70 }, 
                                                                height: { xs: 60, sm: 70 }, 
                                                                bgcolor: theme.palette.primary.main 
                                                            }}
                                                        >
                                                            {supervisor.name?.charAt(0) || <Person />}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="h6" sx={{ 
                                                                fontWeight: 'bold',
                                                                fontSize: { xs: '0.9rem', sm: '1rem' }
                                                            }}>
                                                                {supervisor.name}
                                                            </Typography>
                                                            <Chip
                                                                label={supervisor.academic_title}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                                sx={{ mt: 0.5 }}
                                                            />
                                                        </Box>
                                                    </Box>

                                                    <Divider sx={{ my: 1.5 }} />

                                                    {/* Details */}
                                                    <Stack spacing={1.5}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                            <School fontSize="small" color="action" />
                                                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                {supervisor.university || 'University not specified'}
                                                            </Typography>
                                                        </Box>
                                                        
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                            <Assessment fontSize="small" color="action" />
                                                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                Expertise Match: {record.expertise_match_percentage || 0}%
                                                            </Typography>
                                                            <LinearProgress 
                                                                variant="determinate" 
                                                                value={record.expertise_match_percentage || 0}
                                                                sx={{ width: { xs: 60, sm: 80 }, height: 6, borderRadius: 3 }}
                                                                color={scoreColor}
                                                            />
                                                        </Box>

                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                            <Timeline fontSize="small" color="action" />
                                                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                Completion Rate: {record.completion_rate || 0}%
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                            <Speed fontSize="small" color="action" />
                                                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                Feedback Time: {record.average_feedback_time_days || 0} days
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                            {record.availability_status ? (
                                                                <CheckCircle fontSize="small" color="success" />
                                                            ) : (
                                                                <Cancel fontSize="small" color="error" />
                                                            )}
                                                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                {record.availability_status ? 'Available' : 'Currently Unavailable'}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </CardContent>

                                                <CardActions sx={{ 
                                                    p: 2, 
                                                    pt: 0, 
                                                    gap: 1,
                                                    flexDirection: { xs: 'column', sm: 'row' }
                                                }}>
                                                    <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        startIcon={<ViewIcon />}
                                                        onClick={() => handleOpenView(record)}
                                                        size="small"
                                                    >
                                                        View Profile
                                                    </Button>
                                                    {record.student_choice === 'not_decided' && (
                                                        <Button
                                                            fullWidth
                                                            variant="contained"
                                                            color="success"
                                                            startIcon={<Check />}
                                                            onClick={() => handleOpenChoiceDialog(record, choiceOptions[0])}
                                                            size="small"
                                                        >
                                                            Select
                                                        </Button>
                                                    )}
                                                    {record.student_choice === 'selected' && (
                                                        <Chip icon={<CheckCircle />} label="Selected" color="success" size="small" />
                                                    )}
                                                    {record.student_choice === 'declined' && (
                                                        <Chip icon={<Cancel />} label="Declined" color="error" size="small" />
                                                    )}
                                                </CardActions>
                                            </Card>
                                        </Zoom>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </motion.div>

                {/* Filter Options Dialog */}
                <Dialog
                    open={filterOptions}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="sm"
                    disableEscapeKeyDown
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseOptions();
                        }
                    }}
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Filter Options
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: { xs: 1, sm: 2 } }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<RefreshIcon />}
                                onClick={clearFilters}
                                sx={{ textTransform: 'none' }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Show All Recommendations
                            </Button>

                            <Divider>OR</Divider>

                            <Typography variant="subtitle1" sx={{ textAlign: 'center', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                Filter by Match Score
                            </Typography>

                            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                <InputLabel>Minimum Match Score</InputLabel>
                                <Select
                                    name="min_score"
                                    value={filters.min_score}
                                    onChange={handleFilterChange}
                                    label="Minimum Match Score"
                                >
                                    <MenuItem value="">Any Score</MenuItem>
                                    <MenuItem value="90">90% and above</MenuItem>
                                    <MenuItem value="80">80% and above</MenuItem>
                                    <MenuItem value="70">70% and above</MenuItem>
                                    <MenuItem value="60">60% and above</MenuItem>
                                    <MenuItem value="50">50% and above</MenuItem>
                                </Select>
                            </FormControl>

                            <Divider>OR</Divider>

                            <Typography variant="subtitle1" sx={{ textAlign: 'center', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                Filter by Availability
                            </Typography>

                            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                <InputLabel>Availability Status</InputLabel>
                                <Select
                                    name="availability"
                                    value={filters.availability}
                                    onChange={handleFilterChange}
                                    label="Availability Status"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="available">Available</MenuItem>
                                    <MenuItem value="unavailable">Unavailable</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button color="error" onClick={handleCloseOptions} variant="contained" size={isMobile ? "small" : "medium"}>
                            Close
                        </Button>
                        <Button onClick={handleCloseOptions} color="primary" variant="contained" size={isMobile ? "small" : "medium"}>
                            Apply Filters
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* View Supervisor Dialog */}
                <Dialog
                    open={openView}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseView();
                        }
                    }}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="md"
                    disableEscapeKeyDown
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        textAlign: 'center',
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                        <b>Supervisor Profile</b>
                    </DialogTitle>
                    <DialogContent>
                        {selectedMatch && (
                            <Box sx={{ p: { xs: 1, sm: 2 } }}>
                                {/* Header */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 3, 
                                    mb: 3, 
                                    flexWrap: 'wrap',
                                    justifyContent: { xs: 'center', sm: 'flex-start' }
                                }}>
                                    <Avatar
                                        src={selectedMatch.supervisor?.profile_image}
                                        sx={{ 
                                            width: { xs: 80, sm: 100 }, 
                                            height: { xs: 80, sm: 100 }, 
                                            bgcolor: theme.palette.primary.main 
                                        }}
                                    >
                                        {selectedMatch.supervisor?.name?.charAt(0) || <Person />}
                                    </Avatar>
                                    <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                                        <Typography variant="h5" sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1.1rem', sm: '1.5rem' }
                                        }}>
                                            {selectedMatch.supervisor?.name}
                                        </Typography>
                                        <Typography variant="subtitle1" color="primary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                            {selectedMatch.supervisor?.academic_title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {selectedMatch.supervisor?.university}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        icon={<TrendingUp />}
                                        label={`Match Score: ${selectedMatch.match_score}%`}
                                        color={getScoreColor(selectedMatch.match_score)}
                                        sx={{ ml: { sm: 'auto' }, mt: { xs: 1, sm: 0 } }}
                                    />
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {/* Match Analysis */}
                                <Typography variant="h6" sx={{ 
                                    mb: 2, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                    fontSize: { xs: '1rem', sm: '1.25rem' }
                                }}>
                                    <Analytics /> Match Analysis
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={6} sm={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: theme.palette.info.light + '10' }}>
                                            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                                {selectedMatch.expertise_match_percentage || 0}%
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                Expertise Match
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: theme.palette.success.light + '10' }}>
                                            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                                {selectedMatch.completion_rate || 0}%
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                Completion Rate
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: theme.palette.warning.light + '10' }}>
                                            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                                {selectedMatch.average_feedback_time_days || 0}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                Avg Feedback (days)
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: theme.palette.secondary.light + '10' }}>
                                            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                                {selectedMatch.publication_support_score || 0}%
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                Publication Support
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                {/* Reasoning Summary */}
                                {selectedMatch.reasoning_summary && (
                                    <Accordion defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Lightbulb color="warning" />
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                                    Why This Supervisor?
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography variant="body2" sx={{ 
                                                whiteSpace: 'pre-wrap',
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                            }}>
                                                {selectedMatch.reasoning_summary}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Biography */}
                                {selectedMatch.supervisor?.biography && (
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Description color="info" />
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                                    Biography
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography variant="body2" sx={{ 
                                                whiteSpace: 'pre-wrap',
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                            }}>
                                                {selectedMatch.supervisor?.biography}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Expertise */}
                                {selectedMatch.supervisor?.expertise && (
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Psychology color="secondary" />
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                                    Research Expertise
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography variant="body2" sx={{ 
                                                whiteSpace: 'pre-wrap',
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                            }}>
                                                {selectedMatch.supervisor?.expertise}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button color="error" onClick={handleCloseView} variant="contained" size={isMobile ? "small" : "medium"}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Choice Dialog */}
                <Dialog
                    open={openChoiceDialog}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseChoiceDialog();
                        }
                    }}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="sm"
                    disableEscapeKeyDown
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        textAlign: 'center',
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                        <b>{selectedChoice?.name} Supervisor</b>
                    </DialogTitle>
                    <DialogContent>
                        {selectedMatch && (
                            <Box sx={{ p: { xs: 1, sm: 2 } }}>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                        <strong>Supervisor:</strong> {selectedMatch.supervisor?.name}<br/>
                                        <strong>Match Score:</strong> {selectedMatch.match_score}%
                                    </Typography>
                                </Alert>

                                {selectedChoice?.id === 'requested_change' && (
                                    <TextField
                                        fullWidth
                                        label="Additional Notes / Reason for Change Request"
                                        multiline
                                        rows={4}
                                        value={studentNotes}
                                        onChange={(e) => setStudentNotes(e.target.value)}
                                        placeholder="Please provide details about why you're requesting a change..."
                                        sx={{ mt: 2 }}
                                        size={isMobile ? "small" : "medium"}
                                    />
                                )}

                                {selectedChoice?.id === 'declined' && (
                                    <TextField
                                        fullWidth
                                        label="Reason for Declining (Optional)"
                                        multiline
                                        rows={3}
                                        value={studentNotes}
                                        onChange={(e) => setStudentNotes(e.target.value)}
                                        placeholder="Please provide feedback to help improve future recommendations..."
                                        sx={{ mt: 2 }}
                                        size={isMobile ? "small" : "medium"}
                                    />
                                )}

                                {selectedChoice?.id === 'selected' && (
                                    <Alert severity="success" sx={{ mt: 2 }}>
                                        You are about to select this supervisor. They will be notified of your interest.
                                    </Alert>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button 
                            color="error" 
                            onClick={handleCloseChoiceDialog} 
                            variant="outlined" 
                            disabled={actionLoading}
                            size={isMobile ? "small" : "medium"}
                        >
                            Cancel
                        </Button>
                        <Button 
                            color={selectedChoice?.color} 
                            onClick={handleSubmitChoice} 
                            variant="contained"
                            disabled={actionLoading}
                            startIcon={actionLoading ? <CircularProgress size={20} /> : selectedChoice?.icon}
                            size={isMobile ? "small" : "medium"}
                        >
                            Confirm {selectedChoice?.name}
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default SuggestedSupervisors;